import { useEffect, useState } from "react";
import api, { errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogClose, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { LinkIcon, Copy, Check, Trash2, Store, CalendarDays, BookOpen } from "lucide-react";

// Admin "Invites" tab — generate role-scoped one-time sign-up links for
// vendors, event creators, and marketplace users. The invite is a public URL
// (`/role-invite/:token`) that anyone can claim exactly once within 30 days.
// Parents are not invitable; sign-up for parents is already instant.
const ROLE_OPTIONS = [
  { value: "vendor", label: "Vendor", icon: Store },
  { value: "event_creator", label: "Event Creator", icon: CalendarDays },
  { value: "marketplace_user", label: "Marketplace Seller", icon: BookOpen },
];

const STATUS_STYLES = {
  active: "bg-sage-light text-sage-dark",
  used: "bg-softstone/80 text-forest/70",
  revoked: "bg-terracotta-light text-terracotta-dark",
  expired: "bg-amber-100 text-amber-800",
};

export default function InvitesTab() {
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [role, setRole] = useState("vendor");
  const [generating, setGenerating] = useState(false);
  const [created, setCreated] = useState(null); // invite doc shown in the "copy link" modal right after generation
  const [copiedToken, setCopiedToken] = useState(null); // flashes a check-mark on the copied row

  const load = async () => {
    try {
      const { data } = await api.get("/admin/invites");
      setInvites(data);
    } catch (e) {
      toast.error(errMsg(e, "Could not load invites"));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const generate = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post("/admin/invites", { role });
      setCreated(data);
      await load();
      toast.success("Invite link generated");
    } catch (e) {
      toast.error(errMsg(e, "Could not create invite"));
    } finally {
      setGenerating(false);
    }
  };

  const copy = async (url, token) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopiedToken(token);
      toast.success("Link copied to clipboard");
      setTimeout(() => setCopiedToken((c) => (c === token ? null : c)), 1800);
    } catch {
      toast.error("Clipboard not available — select the link manually");
    }
  };

  const revoke = async (invite) => {
    if (!window.confirm(`Revoke this ${invite.role_label} invite link? It will stop working for anyone who still has the URL.`)) return;
    try {
      await api.post(`/admin/invites/${invite.invite_id}/revoke`);
      toast.success("Invite revoked");
      await load();
    } catch (e) {
      toast.error(errMsg(e, "Could not revoke invite"));
    }
  };

  const resetUsed = async (invite) => {
    const name = invite.used_email || "that user";
    if (!window.confirm(
      `Reset this used ${invite.role_label} invite?\n\n` +
      `This permanently deletes the account that was created with it (${name}) and every event, listing, RSVP, message, subscription and file linked to them, then revives the invite link so the originally-invited person can go through sign-up again.\n\n` +
      `This cannot be undone.`
    )) return;
    try {
      await api.post(`/admin/invites/${invite.invite_id}/reset-used`);
      toast.success("Invite reset — original invitee can sign up again");
      await load();
    } catch (e) {
      toast.error(errMsg(e, "Could not reset invite"));
    }
  };

  return (
    <div className="space-y-6" data-testid="invites-tab">
      <div className="bg-white/80 border border-sage/20 rounded-3xl p-6 shadow-sm" data-testid="invites-generator">
        <h3 className="font-heading font-bold text-forest mb-2 flex items-center gap-2">
          <LinkIcon className="w-5 h-5 text-sage-dark" /> Generate a new invite link
        </h3>
        <p className="text-sm text-forest/60 mb-4">
          Create a one-time, 30-day sign-up link for a specific role. The invited user still goes through email verification, admin approval, and payment — the link just pre-tags their role.
        </p>
        <div className="flex flex-wrap items-end gap-3">
          <div className="min-w-[220px]">
            <label className="text-xs font-bold uppercase tracking-wide text-forest/50 block mb-1">Role</label>
            <Select value={role} onValueChange={setRole}>
              <SelectTrigger className="rounded-xl bg-softstone/60 border-sage/30 w-56" data-testid="invite-role-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ROLE_OPTIONS.map((r) => (
                  <SelectItem key={r.value} value={r.value} data-testid={`invite-role-option-${r.value}`}>
                    {r.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button onClick={generate} disabled={generating} className="rounded-full bg-sage hover:bg-sage-dark text-white" data-testid="invite-generate-btn">
            <LinkIcon className="w-4 h-4 mr-1" /> {generating ? "Generating…" : "Generate link"}
          </Button>
        </div>
      </div>

      <div className="bg-white/80 border border-sage/20 rounded-3xl shadow-sm overflow-hidden" data-testid="invites-table">
        {loading ? (
          <div className="px-5 py-8 text-center text-sm text-forest/50" data-testid="invites-loading">Loading invites…</div>
        ) : invites.length === 0 ? (
          <div className="px-5 py-8 text-center text-sm text-forest/50" data-testid="invites-empty">
            No invite links yet. Generate one above to share with a vendor, event creator or marketplace seller.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-softstone/70 text-left text-forest/60 text-xs uppercase tracking-wide">
                  <th className="px-5 py-3">Role</th>
                  <th className="px-5 py-3">Created</th>
                  <th className="px-5 py-3">Expires</th>
                  <th className="px-5 py-3">Status</th>
                  <th className="px-5 py-3">Used by</th>
                  <th className="px-5 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {invites.map((inv) => (
                  <tr key={inv.invite_id} className="border-t border-sage/10" data-testid={`invite-row-${inv.invite_id}`}>
                    <td className="px-5 py-3 font-semibold text-forest" data-testid={`invite-role-${inv.invite_id}`}>{inv.role_label}</td>
                    <td className="px-5 py-3 text-xs text-forest/60">{inv.created_at ? new Date(inv.created_at).toLocaleString() : "—"}</td>
                    <td className="px-5 py-3 text-xs text-forest/60">{inv.expires_at ? new Date(inv.expires_at).toLocaleDateString() : "—"}</td>
                    <td className="px-5 py-3">
                      <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wide ${STATUS_STYLES[inv.status] || STATUS_STYLES.active}`} data-testid={`invite-status-${inv.invite_id}`}>
                        {inv.status}
                      </span>
                    </td>
                    <td className="px-5 py-3 text-xs text-forest/60" data-testid={`invite-used-by-${inv.invite_id}`}>{inv.used_email || "—"}</td>
                    <td className="px-5 py-3 text-right">
                      <div className="inline-flex items-center gap-1">
                        {inv.status === "active" && (
                          <>
                            <button
                              onClick={() => copy(inv.url, inv.token)}
                              className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full text-xs font-semibold text-sage-dark hover:bg-sage-light transition"
                              data-testid={`invite-copy-${inv.invite_id}`}
                              aria-label="Copy invite link"
                            >
                              {copiedToken === inv.token ? <><Check className="w-3.5 h-3.5" /> Copied</> : <><Copy className="w-3.5 h-3.5" /> Copy link</>}
                            </button>
                            <button
                              onClick={() => revoke(inv)}
                              className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full text-xs font-semibold text-terracotta-dark hover:bg-terracotta-light transition"
                              data-testid={`invite-revoke-${inv.invite_id}`}
                              aria-label="Revoke invite"
                            >
                              <Trash2 className="w-3.5 h-3.5" /> Revoke
                            </button>
                          </>
                        )}
                        {inv.status === "used" && (
                          <button
                            onClick={() => resetUsed(inv)}
                            className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full text-xs font-semibold text-terracotta-dark hover:bg-terracotta-light transition"
                            data-testid={`invite-reset-${inv.invite_id}`}
                            aria-label="Delete used invite and reset for original invitee"
                          >
                            <Trash2 className="w-3.5 h-3.5" /> Delete &amp; reset
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Post-generation modal with the fresh invite URL ready to copy. Admin
          can also close and copy later from the table. */}
      <Dialog open={!!created} onOpenChange={(o) => !o && setCreated(null)}>
        <DialogContent className="max-w-lg" data-testid="invite-created-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-forest flex items-center gap-2">
              <LinkIcon className="w-5 h-5 text-sage-dark" /> Invite link ready
            </DialogTitle>
            <DialogDescription>
              Share this link with the <strong>{created?.role_label}</strong> you're inviting. It works once and expires {created?.expires_at ? new Date(created.expires_at).toLocaleDateString() : "in 30 days"}.
            </DialogDescription>
          </DialogHeader>
          <div className="rounded-xl bg-softstone/70 border border-sage/20 p-3 text-xs text-forest/80 break-all font-mono" data-testid="invite-created-url">
            {created?.url}
          </div>
          <DialogFooter>
            <Button
              type="button"
              onClick={() => created && copy(created.url, created.token)}
              className="rounded-full bg-sage hover:bg-sage-dark text-white"
              data-testid="invite-created-copy-btn"
            >
              {copiedToken === created?.token ? <><Check className="w-4 h-4 mr-1" /> Copied</> : <><Copy className="w-4 h-4 mr-1" /> Copy link</>}
            </Button>
            <DialogClose asChild>
              <Button type="button" variant="outline" className="rounded-full" data-testid="invite-created-close-btn">
                Done
              </Button>
            </DialogClose>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
